import findIndex from 'lodash/findIndex';
import { useEffect } from 'react';

// ShipmentId for open orders is: referenceNo-LID
// This function will remove the LID from ShipmentId.
export const getShipmentHeader = (shipmentId) => {
  const delimiter = '-';

  if (shipmentId.includes(delimiter)) {
    // either redirect or open order
    const parts = shipmentId.split(delimiter);
    // redirects start with R
    if (parts[parts.length - 1].startsWith('R')) {
      return shipmentId;
    }
    parts.pop(); // remove last element (LID)
    return parts.join(delimiter);
  }
  return shipmentId;
};

export const sumProducts = (dropDetails) => {
  const products = [];

  dropDetails.forEach((dropDetail) => {
    dropDetail.DROPS.forEach((drop) => {
      const index = findIndex(products, ['Name', drop.Product]);
      if (index === -1) {
        // Add a new value
        products.push({
          Name: drop.Product,
          QuantityAmbient: parseInt(drop.QuantityAmbient, 10),
          QuantityStandard: parseInt(drop.QuantityStandard, 10),
          Compartments: '',
        });
      } else {
        // Sum with existing value
        products[index].QuantityAmbient += parseInt(drop.QuantityAmbient, 10);
        products[index].QuantityStandard += parseInt(drop.QuantityStandard, 10);
      }
    });
  });

  return products;
};

export const withProductTypename = (products) => products.map(
  (prod) => ({ ...prod, ...{ __typename: 'Product' } }),
);

export const createFileWithNewName = (bits, name, options) => {
  try {
    // If this fails, we go for Blob
    return new File(bits, name, options);
  } catch (e) {
    // If we're here a new File could not be constructed
    const myBlob = new Blob(bits, options || {});
    myBlob.lastModified = new Date();
    myBlob.name = name;
    return myBlob;
  }
};

export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const base64ToFile = (base64String, fileName) => {
  // Split metadata and data
  const arr = base64String.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mimeType = mimeMatch ? mimeMatch[1] : "";

  // Decode base64
  const byteString = atob(arr[1]);
  const byteNumbers = new Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  // Create File
  return new File([byteArray], fileName, { type: mimeType });
};

export function useOnlineEffect(callbackFunction) {
  useEffect(() => {
    // 1. Define the event handler
    const handleOnline = () => {
      if (typeof callbackFunction === 'function') {
        callbackFunction();
      }
    };

    // 2. Attach listener to the window object
    window.addEventListener('online', handleOnline);

    // 3. Clean up the listener when component unmounts
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [callbackFunction]); // Re-run if the function changes
}

export const isImageBlurred = (fileInput) => new Promise((resolve, reject) => {    
  const BLUR_VARIANCE_THRESHOLD = 100;
  const imageUrl = URL.createObjectURL(fileInput);
  const image = new Image();

  image.onload = () => {
    try {
      const maxWidth = 640;
      const scale = Math.min(1, maxWidth / image.width);
      const canvas = document.createElement('canvas');
      const width = Math.max(3, Math.floor(image.width * scale));
      const height = Math.max(3, Math.floor(image.height * scale));
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, width, height);

      const { data } = context.getImageData(0, 0, width, height);
      const gray = new Float32Array(width * height);

      for (let i = 0; i < gray.length; i += 1) {
        const pixelOffset = i * 4;
        gray[i] =
          (0.299 * data[pixelOffset])
          + (0.587 * data[pixelOffset + 1])
          + (0.114 * data[pixelOffset + 2]);
      }

      let sum = 0;
      let sumSq = 0;
      let count = 0;

      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const idx = (y * width) + x;
          const laplacian =
            gray[idx - width]
            + gray[idx - 1]
            - (4 * gray[idx])
            + gray[idx + 1]
            + gray[idx + width];

          sum += laplacian;
          sumSq += laplacian * laplacian;
          count += 1;
        }
      }

      const mean = sum / count;
      const variance = (sumSq / count) - (mean * mean);
      console.log('Image variance: -->', variance, 'Threshold:', BLUR_VARIANCE_THRESHOLD);
      resolve(variance < BLUR_VARIANCE_THRESHOLD);
    } catch (err) {
      reject(err);
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  };

  image.onerror = () => {
    URL.revokeObjectURL(imageUrl);
    reject(new Error('Unable to read selected image.'));
  };

  image.src = imageUrl;
});

export const bolFileExtension = 'jpg';
